import React, { useState, useEffect } from 'react';
import { ProteinDesignInput } from '../components/ProteinDesignInput';
import { supabase } from '../../../shared/lib/supabase';
import { toast } from 'sonner';
import { FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MoleculeDetailPageProps {
  moleculeId: string;
  runName?: string;
  tabId?: string;
  maskData?: {
    activeMaskTypes: string[];
    maskedResidues: { [key: string]: string[] };
  };
}

interface MoleculeFile {
  id: string;
  file_name: string;
  file_size: number;
  file_url: string;
  file_type: string;
  molecule_name?: string;
  atom_count?: number;
  residue_count?: number;
  chain_count?: number;
  created_at: string;
  expires_at: string;
  storage_path?: string;
  ran_odyssey?: boolean;
}

export function MoleculeDetailPage({ moleculeId, runName, tabId, maskData }: MoleculeDetailPageProps) {
  const [molecule, setMolecule] = useState<MoleculeFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isDeleted, setIsDeleted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDeleted) {
      loadMolecule();
    }
  }, [moleculeId, isDeleted]);

  const loadMolecule = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('molecule_viewer')
        .select('*')
        .eq('id', moleculeId)
        .single();

      if (error) throw error;
      
      // Check if storage_path exists
      if (!data?.storage_path) {
        throw new Error('Invalid molecule data: missing storage path');
      }
      
      setMolecule(data);
    } catch (error) {
      console.error('Error loading molecule:', error);
      toast.error('Failed to load molecular file');
      // Navigate back to the project page if molecule doesn't exist
      setTimeout(() => {
        window.close(); // Try to close the tab
        // If tab doesn't close (some browsers prevent this), navigate to project page
        const projectMatch = window.location.pathname.match(/\/projects\/([^\/]+)/);
        if (projectMatch) {
          navigate(`/projects/${projectMatch[1]}`);
        }
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!molecule) return;
    
    // Check if the molecule has been submitted to Odyssey
    const { data: moleculeData, error: checkError } = await supabase
      .from('molecule_viewer')
      .select('ran_odyssey')
      .eq('id', molecule.id)
      .single();
    
    if (checkError) {
      toast.error('Failed to check molecule status');
      return;
    }
    
    if (moleculeData?.ran_odyssey) {
      toast.error('Cannot delete: This configuration has been submitted to Odyssey pipeline');
      return;
    }
    
    if (!confirm(`Delete ${molecule.file_name}? This cannot be undone.`)) return;

    const toastId = toast.loading('Deleting file...');
    setIsDeleted(true); // Prevent further loading attempts
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // First, delete from storage if storage_path exists
      if (molecule.storage_path) {
        const { error: storageError } = await supabase.storage
          .from('molecule-files')
          .remove([molecule.storage_path]);
        
        if (storageError) {
          console.warn('Storage deletion warning:', storageError);
          // Continue with database deletion even if storage fails
        }
      }

      // Then delete from database
      const { error: dbError } = await supabase
        .from('molecule_viewer')
        .delete()
        .eq('id', molecule.id)
        .eq('user_id', user.id);

      if (dbError) throw dbError;

      toast.success('File deleted successfully', { id: toastId });
      
      // Close the tab or navigate away
      setTimeout(() => {
        window.close(); // Try to close the tab
        // If tab doesn't close (some browsers prevent this), navigate to project page
        const projectMatch = window.location.pathname.match(/\/projects\/([^\/]+)/);
        if (projectMatch) {
          navigate(`/projects/${projectMatch[1]}`);
        }
      }, 1000);
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete file', { id: toastId });
      setIsDeleted(false); // Allow retry if delete failed
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  if (!molecule || isDeleted) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            {isDeleted ? 'File has been deleted' : 'Molecule not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Full-screen ProteinDesignInput with delete functionality */}
      <ProteinDesignInput 
        moleculeId={moleculeId} 
        onDelete={molecule?.ran_odyssey ? undefined : handleDelete}
        fileName={molecule?.file_name}
        initialRunName={runName}
        initialMaskData={maskData}
      />
    </div>
  );
}